import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { SUSTAINABILITY, WHY, PARTNERS, IMAGES } from "@/data/content";
import { motion } from "framer-motion";
import {
  Droplet, ShieldCheck, Recycle, Trash2, Boxes, Network,
  Wrench, FlaskConical, Layers, Combine, Target, Leaf, ArrowRight, Handshake,
} from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const SUS_ICONS = [Droplet, ShieldCheck, Wrench, Trash2, Recycle, Combine];
const WHY_ICONS = [Wrench, FlaskConical, Layers, Network, Target, Leaf];

export function Sustainability() {
  return (
    <section id="sustainability" className="relative py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-emerald-500/15 to-cyan-500/10 blur-2xl" />
              <img src={IMAGES.sustainability} alt="Sustainable aquaculture reservoir" className="relative h-[440px] w-full object-cover rounded-[1.75rem] border border-white/10" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <SectionHeading number="12" eyebrow="Sustainability" title="Responsible farming, efficient resources."
                subtitle="Practical measures focused on using water and resources efficiently and reducing waste across operations." />
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {SUSTAINABILITY.map((s, i) => {
                const Icon = SUS_ICONS[i % SUS_ICONS.length];
                return (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.07 }}
                    className="rounded-2xl border border-white/10 bg-[#071728]/70 p-5 hover:border-emerald-400/40 transition-colors"
                  >
                    <span className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-300 border border-emerald-400/20">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-white">{s.title}</h3>
                    <p className="mt-1 text-xs text-slate-400 leading-relaxed">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhyPartners() {
  return (
    <section id="why" className="relative py-20 lg:py-32 bg-[#04121f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading number="13" eyebrow="Why AquaFionix" title="Reasons to build with us." align="center" />
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => {
            const Icon = WHY_ICONS[i % WHY_ICONS.length];
            return (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
                className="group rounded-3xl border border-white/10 bg-[#071728] p-7 hover:border-cyan-400/40 hover:-translate-y-1.5 transition-all duration-400"
              >
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 text-cyan-300 border border-cyan-400/20 group-hover:scale-105 transition-transform">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Partnerships */}
        <div id="partnerships" className="mt-24 lg:mt-32">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
            <img src={IMAGES.partnerships} alt="Aquaculture partnerships" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[#030D18]/88" />
            <div className="relative p-8 sm:p-12">
              <Reveal>
                <SectionHeading number="14" eyebrow="Partnerships" title="Let's grow together." align="center"
                  subtitle="We invite buyers, suppliers, farmers and strategic partners to work with AquaFionix." />
              </Reveal>
              <div className="mt-9 flex flex-wrap justify-center gap-2.5">
                {PARTNERS.map((p, i) => (
                  <motion.span
                    key={p}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-slate-200 hover:border-cyan-400/40 hover:text-white transition-colors"
                  >
                    {p}
                  </motion.span>
                ))}
              </div>
              <div className="mt-9 flex justify-center">
                <button
                  onClick={() => scrollToId("contact")}
                  data-testid="partner-with-us-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-500 px-7 py-3.5 text-sm font-semibold text-[#03121f] shadow-[0_10px_30px_rgba(0,240,255,0.3)] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Handshake className="h-4 w-4" />
                  Partner With Us
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
